import {
  useCallback,
  useEffect,
  useMemo,
  FC,
  memo,
  useState,
  startTransition,
} from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SimpleTreeView } from '@mui/x-tree-view';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  skipViewingInvestorTagForAccountsQuery,
  useGetAccountsQuery,
} from '@/store/accountsApi';
import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import {
  RenderCategoriesTree,
  ParentGroupTreeItem,
} from './CategoriesTreeView';
import { AccountBase } from '@/models/AccountViewModel';

export interface CategoriesTabProps {
  initialExcludedCategories?: number[];
  setFieldValue: (field: string, value: number[]) => void;
  /** When provided, "Clear all" clears every tab's exclusion filters. */
  onClearAllFilters?: () => void;
}

const CategoriesTab: FC<CategoriesTabProps> = memo(
  ({ initialExcludedCategories = [], setFieldValue, onClearAllFilters }) => {
    const { t } = useTranslation();
    const authIsLoaded = useAuthIsLoaded();
    const [isInitialized, setIsInitialized] = useState(false);
    const [excludedCategories, setExcludedCategories] = useState<number[]>(
      initialExcludedCategories,
    );

    // Sync local state with Formik values when they change (e.g., from saved filter selection)
    useEffect(() => {
      setExcludedCategories(initialExcludedCategories);
    }, [initialExcludedCategories]);

    // Load accounts data internally - component is self-contained
    // Data is stable and never changes, checkbox selections don't affect data loading
    const { data: accountsData, isLoading: accountsLoading } =
      useGetAccountsQuery(
        authIsLoaded
          ? { showClosed: false, ...skipViewingInvestorTagForAccountsQuery }
          : skipToken,
      );

    // Mark as initialized after first load with valid data
    useEffect(() => {
      if (
        !isInitialized &&
        accountsData?.Records &&
        accountsData.Records.length > 0 &&
        !accountsLoading
      ) {
        setIsInitialized(true);
      }
    }, [isInitialized, accountsData, accountsLoading]);

    // Build tree structure from all portfolio accounts so the account type tab
    // reflects the same classifications shown across balance sheet and portfolio.
    const treeStructure = useMemo<ParentGroupTreeItem[]>(() => {
      if (!accountsData?.Records || accountsData.Records.length === 0) {
        return [];
      }

      // Group categories by asset/liability
      const categoryMap = new Map<number, { name: string; isAsset: boolean }>();

      accountsData.Records.forEach((account: AccountBase) => {
        if (!categoryMap.has(account.ClassificationId)) {
          categoryMap.set(account.ClassificationId, {
            name: account.ClassificationName,
            isAsset: account.IsAsset,
          });
        }
      });

      const categories = Array.from(categoryMap.entries()).map(
        ([classificationId, category]) => ({
          id: `cat-${classificationId}`,
          name: category.name,
          classificationId,
        }),
      );

      // Group by Assets and Liabilities
      const assets = categories
        .filter((cat) => {
          const categoryData = categoryMap.get(cat.classificationId);
          return categoryData?.isAsset === true;
        })
        .sort((a, b) => a.name.localeCompare(b.name));

      const liabilities = categories
        .filter((cat) => {
          const categoryData = categoryMap.get(cat.classificationId);
          return categoryData?.isAsset === false;
        })
        .sort((a, b) => a.name.localeCompare(b.name));

      const treeItems: ParentGroupTreeItem[] = [];

      if (assets.length > 0) {
        treeItems.push({
          id: 'assets',
          name: t('assets'),
          isAsset: true,
          categories: assets,
        });
      }

      if (liabilities.length > 0) {
        treeItems.push({
          id: 'liabilities',
          name: t('liabilities'),
          isAsset: false,
          categories: liabilities,
        });
      }

      return treeItems;
    }, [accountsData, t]);

    // Handle category checkbox toggle - updates local state and global state
    // Checkbox selections don't affect data loading
    const handleCategoryChange = useCallback(
      (classificationId: number) => {
        setExcludedCategories((prev) => {
          const isExcluded = prev.includes(classificationId);
          const newExcludedCategories = isExcluded
            ? prev.filter((id) => id !== classificationId)
            : [...prev, classificationId];

          // Update global state in a transition (non-blocking)
          startTransition(() => {
            setFieldValue('excludedCategories', newExcludedCategories);
          });

          return newExcludedCategories;
        });
      },
      [setFieldValue],
    );

    // Handle parent group (Assets/Liabilities) checkbox toggle - controls all children categories
    // Checkbox selections don't affect data loading
    const handleParentGroupToggle = useCallback(
      (_isAsset: boolean, categoryIds: number[]) => {
        setExcludedCategories((prevCategories) => {
          // Count how many children are currently excluded
          const excludedChildrenCount = categoryIds.filter((categoryId) =>
            prevCategories.includes(categoryId),
          ).length;

          // Determine action based on current state:
          // - If all children are included (excludedChildrenCount === 0) → exclude all
          // - If all children are excluded (excludedChildrenCount === categoryIds.length) → include all
          // - If some children are excluded (indeterminate) → include all (make parent checked)
          const shouldExcludeAll = excludedChildrenCount === 0;

          // Calculate new excluded categories array
          const newExcludedCategories = shouldExcludeAll
            ? [...prevCategories, ...categoryIds]
            : prevCategories.filter((id) => !categoryIds.includes(id));

          // Update global state in a transition (non-blocking)
          startTransition(() => {
            setFieldValue('excludedCategories', newExcludedCategories);
          });

          return newExcludedCategories;
        });
      },
      [setFieldValue],
    );

    // Render tree - re-renders when excludedCategories changes to update checkbox states
    const renderedTree = useMemo(() => {
      if (!treeStructure.length || !isInitialized) {
        return null;
      }

      return (
        <RenderCategoriesTree
          treeStructure={treeStructure}
          initialFormikValues={{
            excludedCategories,
          }}
          onCategoryToggle={handleCategoryChange}
          onParentGroupToggle={handleParentGroupToggle}
        />
      );
    }, [
      treeStructure,
      isInitialized,
      excludedCategories,
      handleCategoryChange,
      handleParentGroupToggle,
    ]);

    // Delay rendering until data is loaded and initialized to prevent flash
    if (!isInitialized) {
      if (!accountsData?.Records || accountsData.Records.length === 0) {
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        );
      }
      return null;
    }

    // Show warning if no classifications found after initialization
    if (treeStructure.length === 0) {
      return (
        <Paper sx={{ p: 2, maxHeight: '400px', overflow: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography
              variant='h6'
              color='warning.main'
            >
              ⚠
            </Typography>
            <Typography
              variant='body1'
              sx={{ ml: 1, color: 'warning.main' }}
            >
              {t('noAccountTypesFound')}
            </Typography>
          </Box>
          <Typography
            variant='body2'
            color='text.secondary'
          >
            {t('noAccountTypesFoundDescription')}
          </Typography>
        </Paper>
      );
    }

    return (
      <Box
        sx={{
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0,
        }}
      >
        <SimpleTreeView
          sx={{
            flex: 1,
            overflow: 'auto',
            minHeight: 0,
            '& .MuiTreeItem-group': {
              transition: 'none !important',
            },
            '& .MuiCollapse-root': {
              transition: 'none !important',
            },
            '& .MuiCollapse-entered': {
              transition: 'none !important',
            },
          }}
        >
          {renderedTree}
        </SimpleTreeView>

        <Box sx={{ mt: 2, textAlign: 'right', flexShrink: 0 }}>
          <Button
            size='small'
            onClick={() => {
              if (onClearAllFilters) {
                onClearAllFilters();
              } else {
                setExcludedCategories([]);
                startTransition(() => {
                  setFieldValue('excludedCategories', []);
                });
              }
            }}
          >
            {t('resetFilters')}
          </Button>
        </Box>
      </Box>
    );
  },
  (prevProps, nextProps) => {
    // Re-render when setFieldValue or initial values change
    // This ensures checkboxes reflect excluded filters on mount and when Formik values change
    return (
      prevProps.setFieldValue === nextProps.setFieldValue &&
      prevProps.onClearAllFilters === nextProps.onClearAllFilters &&
      prevProps.initialExcludedCategories ===
        nextProps.initialExcludedCategories
    );
  },
);
CategoriesTab.displayName = 'CategoriesTab';

export default CategoriesTab;
